class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    bindHeaderCartIcon(this);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  /**
   * LoveLab header uses a custom cart icon + .ll-header__cart-count.
   * Do not replace the whole icon with Dawn's cart-icon-bubble markup.
   */
  updateHeaderCartIcon(sectionHtml) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink || !sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const countEl = doc.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    const count = countEl ? parseInt(countEl.textContent, 10) || 0 : 0;

    let badge = cartLink.querySelector('.ll-header__cart-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'll-header__cart-count';
        cartLink.appendChild(badge);
      }
      badge.textContent = String(count);
      badge.style.display = '';
    } else if (badge) {
      badge.remove();
    }
  }

  async ensureSections(parsedState) {
    if (parsedState && parsedState.sections && parsedState.sections['cart-drawer']) {
      return parsedState;
    }

    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      (window.routes && window.routes.cart_url ? window.routes.cart_url.replace(/\/cart\/?$/, '/') : '/');

    try {
      const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`);
      if (!response.ok) throw new Error(`sections fetch ${response.status}`);
      const sections = await response.json();
      return Object.assign({}, parsedState || {}, { sections });
    } catch (error) {
      console.error('Cart sections fallback failed', error);
      return parsedState;
    }
  }

  renderContents(parsedState) {
    // Support both sync call sites and async section fallback
    Promise.resolve(this.ensureSections(parsedState))
      .then((state) => {
        try {
          const drawerInner = this.querySelector('.drawer__inner');
          if (drawerInner) drawerInner.classList.remove('is-empty');
          this.classList.remove('is-empty');

          this.productId = state && state.id;
          const sections = (state && state.sections) || {};

          this.getSectionsToRender().forEach((section) => {
            const html = sections[section.id];
            if (!html) {
              console.warn('[cart-drawer] missing section HTML:', section.id);
              return;
            }

            if (section.id === 'cart-icon-bubble') {
              this.updateHeaderCartIcon(html);
              return;
            }

            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement) return;

            const inner = this.getSectionInnerHTML(html, section.selector || '.shopify-section');
            if (inner == null) return;
            sectionElement.innerHTML = inner;
          });

          // Keep cart-drawer-items empty state in sync
          const items = this.querySelector('cart-drawer-items');
          if (items) items.classList.remove('is-empty');
        } catch (error) {
          console.error('[cart-drawer] renderContents failed', error);
        }

        this.dispatchEvent(new CustomEvent('ll:cart-drawer:updated', { bubbles: true }));
        if (window.LLCartUpsells && typeof window.LLCartUpsells.refresh === 'function') {
          window.LLCartUpsells.refresh();
        }

        setTimeout(() => {
          const overlay = this.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.onclick = null;
            overlay.addEventListener('click', this.close.bind(this));
          }
          this.open();
        });
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

function bindHeaderCartIcon(drawer) {
  const cartLink = document.querySelector('#cart-icon-bubble');
  if (!cartLink || cartLink.dataset.llCartBound === '1') return;
  cartLink.dataset.llCartBound = '1';
  cartLink.setAttribute('role', 'button');
  cartLink.setAttribute('aria-haspopup', 'dialog');
  cartLink.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const host = drawer || document.querySelector('cart-drawer');
    if (host && typeof host.open === 'function') host.open(cartLink);
  });
  cartLink.addEventListener('keydown', (event) => {
    if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
      event.preventDefault();
      const host = drawer || document.querySelector('cart-drawer');
      if (host && typeof host.open === 'function') host.open(cartLink);
    }
  });
}

if (!window.__llCartIconDelegated) {
  window.__llCartIconDelegated = true;
  document.addEventListener(
    'click',
    (event) => {
      const cartLink = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!cartLink) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      drawer.open(cartLink);
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bindHeaderCartIcon());
} else {
  bindHeaderCartIcon();
}

if (typeof CartItems === 'function' && !customElements.get('cart-drawer-items')) {
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Don't fetch main-cart-items (missing on product pages) — refresh drawer via sections
  onCartUpdate() {
    if (this.tagName !== 'CART-DRAWER-ITEMS') return;

    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      (window.routes && window.routes.cart_url
        ? window.routes.cart_url.replace(/\/cart\/?$/, '/')
        : '/');

    fetch(`${root}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        const drawerHtml = sections['cart-drawer'];
        if (drawerHtml) {
          const host = document.getElementById('CartDrawer');
          const target = host && host.querySelector('.drawer__inner');
          const inner = this.getSectionInnerHTML(drawerHtml, '.drawer__inner');
          if (target && inner != null) target.innerHTML = inner;
        }

        if (sections['cart-icon-bubble'] && typeof drawer.updateHeaderCartIcon === 'function') {
          drawer.updateHeaderCartIcon(sections['cart-icon-bubble']);
        }

        drawer.dispatchEvent(new CustomEvent('ll:cart-drawer:updated', { bubbles: true }));
        if (window.LLCartUpsells && typeof window.LLCartUpsells.refresh === 'function') {
          window.LLCartUpsells.refresh();
        }

        fetch(`${routes.cart_url}.js`)
          .then((r) => r.json())
          .then((cart) => {
            const empty = !cart.item_count;
            this.classList.toggle('is-empty', empty);
            drawer.classList.toggle('is-empty', empty);
          })
          .catch(() => {});
      })
      .catch((e) => console.error(e));
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
}
